import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import { ArrowLeft, User, MapPin, Phone, Mail, Users, Save, Loader2 } from 'lucide-react';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { getFormulaFromSize, FORMULAS } from '../lib/utils';
import Logo from '../components/Logo';

export default function ClientProfile() {
  const { user, updateProfile } = useAuth();
  const { config } = useConfig();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    address: user?.address || '',
    postal_code: user?.postal_code || '',
    city: user?.city || '',
    household_size: user?.household_size || 2
  });

  const formula = getFormulaFromSize(formData.household_size);
  const formulaInfo = FORMULAS[formula];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateProfile(formData);
      toast.success('Profil mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      {/* Header */}
      <header className="header-glass sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/client" className="flex items-center gap-2 text-[#5C6A61] hover:text-[#2A312C]">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Retour</span>
            </Link>
            <Logo />
            <div className="w-20" />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-['Cormorant_Garamond'] text-3xl font-medium text-[#2A312C] mb-2">
            Mon profil
          </h1>
          <p className="text-[#5C6A61]">Gérez vos informations personnelles</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal info */}
          <div className="card-default space-y-4">
            <h2 className="font-semibold text-[#2A312C] flex items-center gap-2">
              <User className="w-5 h-5 text-[#A5BBA8]" />
              Informations personnelles
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#2A312C] mb-2 block">Nom complet</Label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-default"
                  data-testid="name-input"
                />
              </div>
              <div>
                <Label className="text-[#2A312C] mb-2 block">Email</Label>
                <div className="input-default bg-[#F9F6F0] text-[#5C6A61] flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {user?.email}
                </div>
                <p className="text-xs text-[#5C6A61] mt-1">L'email ne peut pas être modifié</p>
              </div>
              <div>
                <Label className="text-[#2A312C] mb-2 block">Téléphone</Label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="input-default"
                  placeholder="06 12 34 56 78"
                  data-testid="phone-input"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="card-default space-y-4">
            <h2 className="font-semibold text-[#2A312C] flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#A5BBA8]" />
              Adresse d'intervention
            </h2>
            
            <div>
              <Label className="text-[#2A312C] mb-2 block">Adresse</Label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="input-default"
                placeholder="123 rue de la Cuisine"
                data-testid="address-input"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#2A312C] mb-2 block">Code postal</Label>
                <input
                  type="text"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value.slice(0, 5) })}
                  className="input-default"
                  placeholder="75001"
                  maxLength={5}
                  data-testid="postal-code-input"
                />
              </div>
              <div>
                <Label className="text-[#2A312C] mb-2 block">Ville</Label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="input-default"
                  placeholder="Paris"
                  data-testid="city-input"
                />
              </div>
            </div>
          </div>

          {/* Household */}
          <div className="card-default space-y-4">
            <h2 className="font-semibold text-[#2A312C] flex items-center gap-2">
              <Users className="w-5 h-5 text-[#A5BBA8]" />
              Composition du foyer
            </h2>
            
            <div>
              <Label className="text-[#2A312C] mb-2 block">Nombre de personnes</Label>
              <div className="flex items-center gap-4">
                {[1, 2, 3, 4, 5, 6].map((num) => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => setFormData({ ...formData, household_size: num })}
                    className={`w-12 h-12 rounded-xl font-semibold transition-colors ${
                      formData.household_size === num
                        ? 'bg-[#C86B51] text-white'
                        : 'bg-white border border-[#E3DED5] text-[#2A312C] hover:border-[#A5BBA8]'
                    }`}
                    data-testid={`household-size-${num}`}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="p-4 bg-[#A5BBA8]/10 rounded-xl">
              <p className="font-semibold text-[#2A312C]">Formule correspondante : {formulaInfo.name}</p>
              <p className="text-sm text-[#5C6A61]">{formulaInfo.description}</p>
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
              data-testid="save-profile-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Save className="w-5 h-5 mr-2" />
              )}
              Enregistrer
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
